import { lazy, Suspense } from 'react'
import { Navigate, useLocation, useRoutes } from 'react-router-dom'
import LogoOnlyLayout from 'src/layouts/LogoOnlyLayout'

// import RoleBasedGuard from '../guards/RoleBasedGuard';
// components
import LoadingScreen from '../components/LoadingScreen'
import AuthGuard from '../guards/AuthGuard'
// guards
import GuestGuard from '../guards/GuestGuard'
import AdminGuard from '../guards/AdminGuard'
import DashboardLayout from '../layouts/dashboard'
// layouts
import MainLayout from '../layouts/main'
import FileUploadView from '../views/FileUploadView'
import AnalysisView from '../views/AnalysisView'
import KitPanelUploadView from '../views/KitPanelUploadView'
import PipelineTable from '../views/PipelineTaskHistory/PipelineTable'
import PipelineTaskTable from '../views/PipelineTaskHistory/PipelineTaskTable'
import AdminView from '../views/AdminView'
import EBelgeView from '../views/EBelgeView'
// ----------------------------------------------------------------------

const Loadable = (Component) =>
  function (props) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { pathname } = useLocation()
    const isDashboard = pathname.includes('/libra')

    return (
      <Suspense
        fallback={
          <LoadingScreen
            sx={{
              ...(!isDashboard && {
                top: 0,
                left: 0,
                width: 1,
                zIndex: 9999,
                position: 'fixed',
              }),
            }}
          />
        }
      >
        <Component {...props} />
      </Suspense>
    )
  }

export default function Router() {
  return useRoutes([
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          ),
        },
        { path: 'login-unprotected', element: <Login /> },
        { path: 'register-unprotected', element: <Register /> },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'verify', element: <VerifyCode /> },
      ],
    },
    {
      path: 'libra',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { path: '/', element: <Navigate to="/libra/home" replace /> },
        { path: 'files', element: <FileUploadView /> },
        { path: 'home', element: <AnalysisView /> },
        // Batches of analyses started together from the New analysis panel.
        { path: 'batches', element: <BatchesList /> },
        { path: 'batches/:batchId', element: <BatchPage /> },
        {
          path: 'admin',
          element: (
            <AdminGuard>
              <AdminView />
            </AdminGuard>
          ),
        },
        {
          // Internal billing tooling. Same is_superuser gate as /admin, which
          // is also what the backend endpoints check.
          path: 'ebelge',
          element: (
            <AdminGuard>
              <EBelgeView />
            </AdminGuard>
          ),
        },
        { path: 'kits', element: <KitPanelUploadView /> },
        { path: 'reports', element: <ComingSoon /> },
        { path: 'search', element: <ComingSoon /> },
        { path: 'carrier-report/:carrierAnalysisId', element: <CarrierReportView /> },
        { path: '/variants/:fileId/:sampleName/:currentTableId', element: <VariantsView /> },
        { path: '/variants/:fileId/:sampleName', element: <VariantsView /> },
        { path: '/variants/:fileId', element: <VariantsView /> },
        { path: '/somatic-variants/:analysisWrapperId', element: <SomaticVariantsView /> },
        { path: '/somatic-structural/:analysisWrapperId', element: <SomaticStructuralView /> },
        { path: '/rna/:analysisWrapperId', element: <RnaAnalysisView /> },
        { path: '/sample/:fileId/:sampleName', element: <VariantDashboard /> },
        { path: '/cnv/:analysisWrapperId', element: <CnvDashboard /> },
        { path: '/unified/:analysisWrapperId', element: <UnifiedView /> },
        { path: '/methylation/:analysisWrapperId', element: <MethylationDashboard /> },
        { path: '/cohort/:cohortId', element: <CohortDashboard /> },
        { path: 'payment-success/:paymentId', element: <PaymentResult /> },
        { path: 'payment-failed/:paymentId', element: <PaymentResult /> },
        { path: 'invoice/:paymentId', element: <Invoice /> },
        {
          path: 'user',
          children: [
            { path: '/', element: <Navigate to="/libra/user/list" replace /> },
            //      { path: 'profile', element: <UserProfile /> },
            //    { path: 'cards', element: <UserCards /> },
            { path: 'list', element: <UserList /> },
            { path: 'new', element: <UserCreate /> },
            { path: '/:id/edit', element: <UserCreate /> },
            { path: 'account', element: <UserAccount /> },
          ],
        },
        {
          path: 'pipeline-task-history/pipelines',
          element: <PipelineTable />,
        },
        {
          path: 'pipeline-task-history/pipelines/:pipelineId/tasks',
          element: <PipelineTaskTable />,
        },
      ],
    },
    {
      path: '*',
      element: <Navigate to="/libra/home" replace />,
    },
  ])
}

// IMPORT COMPONENTS

// Authentication
const Login = Loadable(lazy(() => import('../pages/authentication/Login')))
const Register = Loadable(lazy(() => import('../pages/authentication/Register')))
const ResetPassword = Loadable(lazy(() => import('../pages/authentication/ResetPassword')))
const VerifyCode = Loadable(lazy(() => import('../pages/authentication/VerifyCode')))
// Dashboard
const GeneralApp = Loadable(lazy(() => import('../pages/dashboard/GeneralApp')))
const GeneralEcommerce = Loadable(lazy(() => import('../pages/dashboard/GeneralEcommerce')))
const GeneralAnalytics = Loadable(lazy(() => import('../pages/dashboard/GeneralAnalytics')))
const GeneralBanking = Loadable(lazy(() => import('../pages/dashboard/GeneralBanking')))
const GeneralBooking = Loadable(lazy(() => import('../pages/dashboard/GeneralBooking')))
const EcommerceShop = Loadable(lazy(() => import('../pages/dashboard/EcommerceShop')))
const EcommerceProductDetails = Loadable(lazy(() => import('../pages/dashboard/EcommerceProductDetails')))
const EcommerceProductList = Loadable(lazy(() => import('../pages/dashboard/EcommerceProductList')))
const EcommerceProductCreate = Loadable(lazy(() => import('../pages/dashboard/EcommerceProductCreate')))
const EcommerceCheckout = Loadable(lazy(() => import('../pages/dashboard/EcommerceCheckout')))
const EcommerceInvoice = Loadable(lazy(() => import('../pages/dashboard/EcommerceInvoice')))
const BlogPosts = Loadable(lazy(() => import('../pages/dashboard/BlogPosts')))
const BlogPost = Loadable(lazy(() => import('../pages/dashboard/BlogPost')))
const BlogNewPost = Loadable(lazy(() => import('../pages/dashboard/BlogNewPost')))
const UserProfile = Loadable(lazy(() => import('../pages/dashboard/UserProfile')))
const UserCards = Loadable(lazy(() => import('../pages/dashboard/UserCards')))
const UserList = Loadable(lazy(() => import('../pages/dashboard/UserList')))
const PaymentResult = Loadable(lazy(() => import('../pages/dashboard/PaymentResult')))
const Invoice = Loadable(lazy(() => import('../pages/dashboard/Invoice')))
const UserAccount = Loadable(lazy(() => import('../pages/dashboard/UserAccount')))
const UserCreate = Loadable(lazy(() => import('../pages/dashboard/UserCreate')))
const Chat = Loadable(lazy(() => import('../pages/dashboard/Chat')))
const Mail = Loadable(lazy(() => import('../pages/dashboard/Mail')))
const Calendar = Loadable(lazy(() => import('../pages/dashboard/Calendar')))
const Kanban = Loadable(lazy(() => import('../pages/dashboard/Kanban')))
// Main
const LandingPage = Loadable(lazy(() => import('../pages/LandingPage')))
const About = Loadable(lazy(() => import('../pages/About')))
const Contact = Loadable(lazy(() => import('../pages/Contact')))
const Faqs = Loadable(lazy(() => import('../pages/Faqs')))
const ComingSoon = Loadable(lazy(() => import('../pages/ComingSoon')))
const Maintenance = Loadable(lazy(() => import('../pages/Maintenance')))
const Pricing = Loadable(lazy(() => import('../pages/Pricing')))
const Payment = Loadable(lazy(() => import('../pages/Payment')))
const Page500 = Loadable(lazy(() => import('../pages/Page500')))
const NotFound = Loadable(lazy(() => import('../pages/Page404')))
// Components
const ComponentsOverview = Loadable(lazy(() => import('../pages/ComponentsOverview')))
const Color = Loadable(lazy(() => import('../pages/components-overview/foundations/FoundationColors')))
const Typography = Loadable(lazy(() => import('../pages/components-overview/foundations/FoundationTypography')))
const Shadows = Loadable(lazy(() => import('../pages/components-overview/foundations/FoundationShadows')))
const Grid = Loadable(lazy(() => import('../pages/components-overview/foundations/FoundationGrid')))
const Icons = Loadable(lazy(() => import('../pages/components-overview/foundations/FoundationIcons')))
const Accordion = Loadable(lazy(() => import('../pages/components-overview/material-ui/Accordion')))
const Alert = Loadable(lazy(() => import('../pages/components-overview/material-ui/Alert')))
const Autocomplete = Loadable(lazy(() => import('../pages/components-overview/material-ui/Autocomplete')))
const Avatar = Loadable(lazy(() => import('../pages/components-overview/material-ui/Avatar')))
const Badge = Loadable(lazy(() => import('../pages/components-overview/material-ui/Badge')))
const Breadcrumb = Loadable(lazy(() => import('../pages/components-overview/material-ui/Breadcrumb')))
const Buttons = Loadable(lazy(() => import('../pages/components-overview/material-ui/buttons')))
const Checkbox = Loadable(lazy(() => import('../pages/components-overview/material-ui/Checkboxes')))
const Chip = Loadable(lazy(() => import('../pages/components-overview/material-ui/chips')))
const Dialog = Loadable(lazy(() => import('../pages/components-overview/material-ui/dialog')))
const Label = Loadable(lazy(() => import('../pages/components-overview/material-ui/Label')))
const List = Loadable(lazy(() => import('../pages/components-overview/material-ui/Lists')))
const Menu = Loadable(lazy(() => import('../pages/components-overview/material-ui/Menus')))
const Pagination = Loadable(lazy(() => import('../pages/components-overview/material-ui/Pagination')))
const Pickers = Loadable(lazy(() => import('../pages/components-overview/material-ui/pickers')))
const Popover = Loadable(lazy(() => import('../pages/components-overview/material-ui/Popover')))
const Progress = Loadable(lazy(() => import('../pages/components-overview/material-ui/progress')))
const RadioButtons = Loadable(lazy(() => import('../pages/components-overview/material-ui/RadioButtons')))
const Rating = Loadable(lazy(() => import('../pages/components-overview/material-ui/Rating')))
const Slider = Loadable(lazy(() => import('../pages/components-overview/material-ui/Slider')))
const Snackbar = Loadable(lazy(() => import('../pages/components-overview/material-ui/Snackbar')))
const Stepper = Loadable(lazy(() => import('../pages/components-overview/material-ui/stepper')))
const Switches = Loadable(lazy(() => import('../pages/components-overview/material-ui/Switches')))
const Table = Loadable(lazy(() => import('../pages/components-overview/material-ui/table')))
const Tabs = Loadable(lazy(() => import('../pages/components-overview/material-ui/Tabs')))
const Textfield = Loadable(lazy(() => import('../pages/components-overview/material-ui/textfield')))
const Timeline = Loadable(lazy(() => import('../pages/components-overview/material-ui/Timeline')))
const Tooltip = Loadable(lazy(() => import('../pages/components-overview/material-ui/Tooltip')))
const TransferList = Loadable(lazy(() => import('../pages/components-overview/material-ui/transfer-list')))
const TreeView = Loadable(lazy(() => import('../pages/components-overview/material-ui/TreeView')))
const DataGrid = Loadable(lazy(() => import('../pages/components-overview/material-ui/data-grid')))

//
const Charts = Loadable(lazy(() => import('../pages/components-overview/extra/Charts')))
const Map = Loadable(lazy(() => import('../pages/components-overview/extra/Map')))
const Editor = Loadable(lazy(() => import('../pages/components-overview/extra/Editor')))
const CopyToClipboard = Loadable(lazy(() => import('../pages/components-overview/extra/CopyToClipboard')))
const Upload = Loadable(lazy(() => import('../pages/components-overview/extra/Upload')))
const Carousel = Loadable(lazy(() => import('../pages/components-overview/extra/Carousel')))
const MultiLanguage = Loadable(lazy(() => import('../pages/components-overview/extra/MultiLanguage')))
const Animate = Loadable(lazy(() => import('../pages/components-overview/extra/animate')))
const MegaMenu = Loadable(lazy(() => import('../pages/components-overview/extra/MegaMenu')))
const FormValidation = Loadable(lazy(() => import('../pages/components-overview/extra/form-validation')))

const VariantsView = Loadable(lazy(() => import('../views/VariantsView')))
// Somatic variants are a separate view keyed on the analysis wrapper, not on a
// file id: a tumour/normal analysis has no single "the VCF file" to name it by.
const SomaticVariantsView = Loadable(lazy(() => import('../views/SomaticVariantsView')))
// Copy number, SVs and fusions of the same analysis (roadmap R1.37).
const SomaticStructuralView = Loadable(lazy(() => import('../views/SomaticStructuralView')))
// A tumour RNA analysis: library, run QC, gene counts (roadmap R4.1, R4.3a).
const RnaAnalysisView = Loadable(lazy(() => import('../views/RnaAnalysisView')))
const VariantDetailsView = Loadable(lazy(() => import('../views/VariantDetailsView')))
const VariantDashboard = Loadable(lazy(() => import('../views/VariantDashboard')))
const CnvDashboard = Loadable(lazy(() => import('../views/CnvDashboard')))
const UnifiedView = Loadable(lazy(() => import('../views/UnifiedView')))
const MethylationDashboard = Loadable(lazy(() => import('../views/MethylationDashboard')))
const CohortDashboard = Loadable(lazy(() => import('../views/CohortDashboard')))
const CarrierReportView = Loadable(lazy(() => import('../views/CarrierReportView')))
const BatchesList = Loadable(lazy(() => import('../views/Batches/BatchesList')))
const BatchPage = Loadable(lazy(() => import('../views/Batches/BatchPage')))
